import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import {
  Award,
  BadgeDollarSign,
  Calendar,
  CalendarHeart,
  CheckCircle,
  Clock,
  FileText,
  Gift,
  Heart,
  MapPin,
  Shield,
  Star,
  Users,
} from "lucide-react";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";
import vipHero from "@assets/team/isabel-milbourn.jpg";

export default function VipProgram() {
  return (
    <div>
      <SEO
        title="VIP Roster Program | Recurring Dog Walking"
        description="Join the Tallahassee Lassie VIP Roster Program for recurring, reliable dog walking and pet care in Tallahassee. Priority holiday booking, holiday fees waived, automatic scheduling, and guaranteed reliability."
        path="/vip-program"
      />

      <section className="relative py-16 md:py-20 lg:py-24 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <Reveal>
              <div>
                <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-1.5 rounded-full text-sm font-bold tracking-wide uppercase mb-6">
                  <Star size={16} fill="currentColor" /> Premium Care
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6 leading-tight">
                  Reliable Dog Walks for Busy Pet Owners
                </h1>
                <p className="text-xl text-muted-foreground leading-relaxed mb-8">
                  Private walks and drop-in visits from a trained local team, with real-time updates and reliable, consistent care you can count on.
                </p>
                <Button asChild size="lg" className="h-14 text-lg px-8">
                  <a href="https://www.timetopet.com/portal/tallylassiepets/create-account" target="_blank" rel="noopener noreferrer">
                    Book Your Meet and Greet
                  </a>
                </Button>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div className="relative">
                <img
                  src={vipHero}
                  alt="A Tallahassee Lassie team member in a purple shirt walking a dog on a tree-lined path"
                   className="w-full aspect-[4/5] max-h-[42rem] shadow-xl object-cover"
                  style={{ borderRadius: "32px 0 32px 0" }}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Reveal>
              <h2 className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-4">
                You're Busy; We Get It!
              </h2>
              <h3 className="text-xl md:text-2xl font-serif text-primary mb-8">
                 Your schedule is full, but the dog still needs care…
              </h3>
            </Reveal>
            <Reveal delay={100}>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                Between work, meetings, and everything else on your plate, it's hard to be home during the day. That's where we come in!
              </p>
              <p className="text-lg text-foreground/80 leading-relaxed max-w-3xl mx-auto mt-6 font-medium">
                 Tallahassee Lassie provides <strong>reliable, private dog walks and drop-in visits</strong>, designed for busy pet parents who need consistency.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <Reveal>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-4">
                  Set It and Forget It Scheduling
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  VIP Roster members receive reliable recurring care plus added flexibility and priority when plans change.
                </p>
              </div>
            </Reveal>
             <div className="grid md:grid-cols-[0.8fr_1.2fr] gap-8 items-start">
              <Reveal delay={100}>
                 <div className="bg-white p-8 shadow-md border border-border/30" style={{ borderRadius: "24px 0 24px 0" }}>
                  <p className="text-lg text-foreground/80 leading-relaxed mb-6">
                    Most of our clients choose a recurring weekly schedule — so their pets are always cared for, without having to rebook.
                  </p>
                  <p className="text-lg text-foreground/80 leading-relaxed font-medium">
                    It's simple, reliable, and built around you.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={200}>
                 <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: Calendar, text: "Recurring scheduling" },
                    { icon: Clock, text: "Automatic billing" },
                    { icon: Star, text: "On-demand options" },
                    { icon: FileText, text: "Visit reports after each visit" },
                    { icon: MapPin, text: "Trusted and local, based in Tallahassee" },
                    { icon: BadgeDollarSign, text: "Late-booking fees waived" },
                    { icon: CalendarHeart, text: "First priority for holiday and vacation bookings" },
                    { icon: Gift, text: "Birthday perks" },
                  ].map((item, i) => (
                     <div key={i} className="flex items-center gap-4 p-4 bg-white/80 border border-border/20" style={{ borderRadius: "12px 0 12px 0" }}>
                      <div className="flex-shrink-0 w-9 h-9 bg-secondary/10 text-secondary rounded-full flex items-center justify-center">
                        <item.icon size={18} />
                      </div>
                      <span className="text-foreground/80 font-medium">{item.text}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <Reveal>
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-4">
                Not an App. A Professional Team.
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Users, text: "Trained, employed team members (no gig workers)" },
              { icon: Shield, text: "Backup coverage — your pet is never left without care" },
              { icon: CheckCircle, text: "Consistent walks you know and trust" },
              { icon: FileText, text: "Fully bonded and insured" },
              { icon: Calendar, text: "Recurring scheduling and automatic billing" },
              { icon: Award, text: "Award-winning local team" },
              { icon: Heart, text: "Community-service oriented" },
            ].map((item, i) => (
              <Reveal
                key={i}
                delay={i * 80}
                className="last:sm:col-span-2 last:sm:w-[calc(50%-0.75rem)] last:sm:justify-self-center last:lg:col-span-1 last:lg:w-auto last:lg:col-start-2"
              >
                <div className="flex items-start gap-4 p-6 bg-card border border-border/50 hover:border-primary/30 transition-all h-full" style={{ borderRadius: "16px 0 16px 0" }}>
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center">
                    <item.icon size={20} />
                  </div>
                  <p className="text-foreground/80 font-medium leading-relaxed">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl mx-auto bg-primary text-primary-foreground p-10 md:p-14 text-center" style={{ borderRadius: "32px 0 32px 0" }}>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">
                Let's get your dog on a routine
              </h2>
              <p className="text-xl text-primary-foreground/80 mb-2">
                Spots for midday book quicker than morning and evening.
              </p>
              <p className="text-xl text-primary-foreground/80 mb-8 font-medium">
                Book your meet and greet today!
              </p>
              <Button asChild size="lg" variant="secondary" className="h-14 text-lg px-10 bg-white text-primary hover:bg-white/90">
                <a href="https://www.timetopet.com/portal/tallylassiepets/create-account" target="_blank" rel="noopener noreferrer">
                  Book Now
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
